import { FormDrawerContent, FormFetchDrawer, Id, isSuccess, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { applicationService } from "../shared/api/applicationService";
import { eventService } from "../shared/api/event/eventService";
import { ApplicationType, ApplicationUpdateFormValues } from "../shared/types/application";
import { BaseApplicationDataFields } from "./BaseApplicationDataFields";
import { useBaseApplicationFormValidate } from "./baseApplicationFormValidate";

interface Props {
	open: boolean;
	onClose: () => void;
	applicationType: ApplicationType;
	entityId: Id;
	eventId?: Id;
}

export const EditBaseApplicationDrawer: FC<Props> = ({ entityId, eventId, applicationType, ...props }) => {
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const queryClient = useQueryClient();
	const validate = useBaseApplicationFormValidate();

	const updateApplication = useCallback(
		async (values: ApplicationUpdateFormValues) => {
			const response = await applicationService.update(entityId, {
				eventId,
				deletionDate: values.deletionDate,
				setupDate: values.setupDate,
				projectNumber: values.projectNumber,
				subdomain: values.subdomain,
			});

			if (isSuccess(response)) {
				await queryClient.invalidateQueries({ queryKey: eventService.indexKey });
				await queryClient.invalidateQueries({ queryKey: applicationService.keys.index });
				showToast({
					type: "success",
					title: t("Application"),
					body: t("Application was successfully updated."),
				});
				return true;
			}

			return false;
		},
		[entityId, eventId, queryClient, showToast, t],
	);

	return (
		<FormFetchDrawer
			{...props}
			title={t(`Edit ${applicationType}`)}
			queryKey={applicationService.keys.details(entityId)}
			queryFn={() => applicationService.show(entityId).then((res) => res.data.data)}
			validate={validate}
			onSubmit={updateApplication}
			renderSuccess={(props) => (
				<FormDrawerContent {...props}>
					<BaseApplicationDataFields />
				</FormDrawerContent>
			)}
		/>
	);
};
